import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import FormikTextField from '../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../shared/components/formik/FormikSelectField'
import FormikCheckboxField from '../../../shared/components/formik/FormikCheckboxField'
import FormikNumericField from '../../../shared/components/formik/FormikNumericField'
import useServicesResources from '../../../shared/resources/api/v1/admin/docker/useServicesResources'
import { useTeam } from '../../../contexts/TeamContext'
import { useProject } from '../../../contexts/ProjectContext'
import { useServices } from '../../../contexts/ServicesContext'
import PageBreadcrumbs from '../../../shared/components/PageBreadcrumbs'

function ServiceCreatePage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const { teamSlug, projectSlug } = useParams()
  const { team } = useTeam()
  const { project } = useProject()
  const { reload: reloadServices } = useServices()
  const { create } = useServicesResources(teamSlug, projectSlug)

  const expectedStateOptions = useMemo(() => {
    return [
      { value: 'running', label: intl.formatMessage({ id: 'dockerServices.expectedStates.running' }) },
      { value: 'stopped', label: intl.formatMessage({ id: 'dockerServices.expectedStates.stopped' }) }
    ]
  }, [intl])

  const restartPolicyOptions = useMemo(() => {
    return [
      { value: '', label: '' },
      { value: 'no', label: intl.formatMessage({ id: 'dockerServices.restartPolicies.no' }) },
      { value: 'always', label: intl.formatMessage({ id: 'dockerServices.restartPolicies.always' }) },
      { value: 'on-failure', label: intl.formatMessage({ id: 'dockerServices.restartPolicies.onFailure' }) },
      { value: 'unless-stopped', label: intl.formatMessage({ id: 'dockerServices.restartPolicies.unlessStopped' }) }
    ]
  }, [intl])

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    image: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    expectedState: Yup.string(),
    logStreaming: Yup.boolean(),
    restartPolicy: Yup.string(),
    restartMaxRetries: Yup.number().nullable(),
    command: Yup.string(),
    containerName: Yup.string(),
    platform: Yup.string()
  })

  const formik = useFormik({
    initialValues: {
      name: '',
      image: '',
      expectedState: 'running',
      logStreaming: false,
      restartPolicy: '',
      restartMaxRetries: null,
      command: '',
      containerName: '',
      platform: ''
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const serviceData = {
        service: {
          name: values.name,
          image: values.image,
          expectedState: values.expectedState,
          logStreaming: values.logStreaming,
          restartPolicy: values.restartPolicy || null,
          restartMaxRetries: values.restartPolicy === 'on-failure' ? values.restartMaxRetries : null,
          command: values.command || null,
          containerName: values.containerName || null,
          platform: values.platform || null
        }
      }

      create(serviceData)
        .then(() => {
          reloadServices()
          navigate(`/teams/${team.slug}/docker/projects/${project.slug}`)
        })
        .catch((err) => {
          setStatus(err.message || 'Error creating service')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <PageBreadcrumbs entities={{ team, project }} />
        <Typography variant="h4" component="h1" gutterBottom>
          <FormattedMessage id="dockerServices.create.title" />
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit}>
          <FormikTextField
            formik={formik}
            name="name"
            label={<FormattedMessage id="dockerServices.create.nameLabel" />}
            autoFocus
          />

          <Box sx={{ mt: 2 }}>
            <FormikTextField
              formik={formik}
              name="image"
              label={<FormattedMessage id="dockerServices.create.imageLabel" />}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormikSelectField
              formik={formik}
              name="expectedState"
              label={<FormattedMessage id="dockerServices.create.expectedStateLabel" />}
              options={expectedStateOptions}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormikCheckboxField
              formik={formik}
              name="logStreaming"
              label={<FormattedMessage id="dockerServices.create.logStreamingLabel" />}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormikSelectField
              formik={formik}
              name="restartPolicy"
              label={<FormattedMessage id="dockerServices.create.restartPolicyLabel" />}
              options={restartPolicyOptions}
            />
          </Box>

          {formik.values.restartPolicy === 'on-failure' && (
            <Box sx={{ mt: 2 }}>
              <FormikNumericField
                formik={formik}
                name="restartMaxRetries"
                label={<FormattedMessage id="dockerServices.create.restartMaxRetriesLabel" />}
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <FormikTextField
              formik={formik}
              name="command"
              label={<FormattedMessage id="dockerServices.create.commandLabel" />}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormikTextField
              formik={formik}
              name="containerName"
              label={<FormattedMessage id="dockerServices.create.containerNameLabel" />}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormikTextField
              formik={formik}
              name="platform"
              label={<FormattedMessage id="dockerServices.create.platformLabel" />}
            />
          </Box>

          {formik.status && (
            <Typography color="error" sx={{ mt: 2 }}>
              {formik.status}
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={formik.isSubmitting}
            sx={{ mt: 3 }}
          >
            <FormattedMessage id="dockerServices.create.submitButton" />
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default ServiceCreatePage
